import React, { useState } from 'react';
import type { GuildEmoji } from '../../types';
import { Popover, PopoverTrigger, PopoverContent, PopoverClose } from '../ui/popover';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { ScrollArea } from '../ui/scroll-area';

interface EmojiPickerProps {
  value: string;
  onChange: (emoji: string) => void;
  guildEmojis: GuildEmoji[];
  disabled?: boolean;
}

// Common Discord-compatible emojis
const COMMON_EMOJIS = [
  '😀', '😃', '😄', '😁', '😆', '😅', '🤣', '😂',
  '🙂', '🙃', '😉', '😊', '😇', '🥰', '😍', '🤩',
  '😘', '😗', '😚', '😙', '😋', '😛', '😜', '🤪',
  '🎮', '🎯', '🎲', '🎨', '🎬', '🎤', '🎧', '🎼',
  '🎵', '🎶', '🎹', '🎺', '🎻', '🎸', '🥁', '🎭',
  '⭐', '🌟', '💫', '✨', '💥', '💢', '💤', '💨',
  '🔥', '❄️', '💧', '💦', '🌊', '🎃', '🎄', '🎆',
  '🎇', '🎈', '🎉', '🎊', '🎋', '🎍', '🎎', '🎏',
  '🎐', '🎑', '🧧', '🎀', '🎁', '🎗️', '🎟️', '🎖️',
  '🏆', '🏅', '🥇', '🥈', '🥉', '⚽', '⚾', '🏀',
  '🏐', '🏈', '🏉', '🎾', '🥏', '🎳', '🏏', '🏑',
  '🏒', '🥍', '🏓', '🏸', '🥊', '🥋', '🥅', '⛳',
  '🎣', '🤿', '🎽', '🎿', '🛷', '🥌', '🎯', '🎮',
  '🕹️', '🎰', '🎲', '🧩', '🎭', '🎨', '🧵', '🧶',
  '👑', '💎', '💍', '💄', '👗', '👔', '🎩', '🧢',
  '👓', '🕶️', '🥽', '🥼', '🦺', '👞', '👟', '🥾',
  '🥿', '👠', '👡', '👢', '👒', '🎓', '⛑️', '🪖',
];

// Renders an emoji string — custom Discord emojis as <img>, unicode as text
export const EmojiDisplay: React.FC<{ emoji: string; className?: string }> = ({ emoji, className = 'w-5 h-5' }) => {
  const match = emoji.match(/^<(a?):(\w+):(\d+)>$/);
  if (match) {
    const [, animated, name, id] = match;
    const ext = animated ? 'gif' : 'webp';
    return (
      <img
        src={`https://cdn.discordapp.com/emojis/${id}.${ext}?size=32&quality=lossless`}
        alt={`:${name}:`}
        className={`inline-block object-contain ${className}`}
      />
    );
  }
  return <span>{emoji}</span>;
};

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  value,
  onChange,
  guildEmojis,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleEmojiSelect = (emoji: string) => {
    onChange(emoji);
    setIsOpen(false);
  };

  const handleCustomEmojiSelect = (emoji: GuildEmoji) => {
    // Format: <:name:id> for custom Discord emojis
    const emojiString = emoji.animated ? `<a:${emoji.name}:${emoji.id}>` : `<:${emoji.name}:${emoji.id}>`;
    onChange(emojiString);
    setIsOpen(false);
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hover:bg-surface-container-highest rounded cursor-pointer text-lg w-8 h-8 flex items-center justify-center"
          disabled={disabled}
          aria-label="Open emoji picker"
        >
          <EmojiDisplay emoji={value || '🎭'} className="w-5 h-5" />
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-64">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">
            Select Emoji
          </span>
          <PopoverClose asChild>
            <button
              type="button"
              className="text-on-surface-variant hover:text-on-surface transition-colors cursor-pointer"
              aria-label="Close"
            >
              <span className="material-symbols-outlined text-[18px]">close</span>
            </button>
          </PopoverClose>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="common">
          <TabsList className="w-full">
            <TabsTrigger value="common" className="flex-1">Common</TabsTrigger>
            <TabsTrigger value="server" className="flex-1">
              Server ({guildEmojis.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="common">
            <ScrollArea className="max-h-48">
              <div className="grid grid-cols-6 gap-2">
                {COMMON_EMOJIS.map((emoji, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className="w-8 h-8 flex items-center justify-center hover:bg-surface-container-highest rounded cursor-pointer text-lg"
                    onClick={() => handleEmojiSelect(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </ScrollArea>
          </TabsContent>

          <TabsContent value="server">
            <ScrollArea className="max-h-48">
              {guildEmojis.length === 0 ? (
                <p className="text-sm text-on-surface-variant text-center py-4">
                  No custom emojis in this server
                </p>
              ) : (
                <div className="grid grid-cols-6 gap-2">
                  {guildEmojis.map((emoji) => (
                    <button
                      key={emoji.id}
                      type="button"
                      className="w-8 h-8 flex items-center justify-center hover:bg-surface-container-highest rounded cursor-pointer text-lg"
                      onClick={() => handleCustomEmojiSelect(emoji)}
                      title={`:${emoji.name}:`}
                    >
                      <img
                        src={`https://cdn.discordapp.com/emojis/${emoji.id}.${emoji.animated ? 'gif' : 'png'}?size=32&quality=lossless`}
                        alt={emoji.name}
                        className="w-6 h-6 object-contain"
                      />
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};
